import { supabase } from '../lib/supabaseClient';
import {
  currentTarget,
  type MeasureDirection,
  type MeasurePoint,
  type MeasurePointKind,
  type MeasureRole,
} from '../utils/issueMasp';
import { type CauseCheckAnswer, type CauseCheckView } from '../utils/causeWork';
import { standardizationEventPayload } from '../utils/standardization';

export type { MeasureDirection, MeasureRole };

export interface IssueMeasurePointRecord extends MeasurePoint {
  id: string;
  measureId: string;
}

interface IssueMeasurePointRow {
  id: string;
  measure_id: string;
  point_kind: MeasurePointKind;
  value: number | string;
  occurred_on: string;
  created_at: string;
}

function toRecord(row: IssueMeasurePointRow): IssueMeasurePointRecord {
  return {
    id: row.id,
    measureId: row.measure_id,
    pointKind: row.point_kind,
    value: Number(row.value),
    occurredOn: row.occurred_on,
    createdAt: row.created_at,
  };
}

export interface IssueMeasureRecord {
  id: string;
  role: MeasureRole;
  name: string;
  unit: string;
  direction: MeasureDirection;
  points: IssueMeasurePointRecord[];
}

export interface StratumSliceRecord {
  id: string;
  label: string;
  occurrenceCount: number | null;
  amount: number | null;
  measureId: string | null;
}

export interface StratumRecord {
  id: string;
  dimension: string;
  slices: StratumSliceRecord[];
}

export interface GembaNoteRecord {
  id: string;
  fact: string;
  place: string | null;
  observedAt: string;
  observerId: string | null;
}

export interface IssueMaspBoard {
  measures: IssueMeasureRecord[];
  strata: StratumRecord[];
  gemba: GembaNoteRecord[];
}

function numeric(value: number | string | null): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function fail(error: { code?: string; message?: string } | null, fallback: string): never {
  if (error?.code === '23505') throw new Error('Essa dimensão já existe neste problema.');
  if (error?.code === '23514') throw new Error('O registro não fechou com o problema ou a medida.');
  throw new Error(error?.message || fallback);
}

/** Lê a meta corrente. A ordem não fica na consulta: quem decide é `currentTarget`. */
export async function readCurrentTarget(measureId: string): Promise<IssueMeasurePointRecord | null> {
  const { data, error } = await supabase
    .from('issue_measure_points')
    .select('id, measure_id, point_kind, value, occurred_on, created_at')
    .eq('measure_id', measureId)
    .eq('point_kind', 'target');
  if (error) throw error;
  return currentTarget((data ?? []).map((row) => toRecord(row as IssueMeasurePointRow)));
}

export async function loadIssueMaspBoard(organizationId: string, issueId: string): Promise<IssueMaspBoard> {
  const [measuresResult, pointsResult, strataResult, slicesResult, gembaResult] = await Promise.all([
    supabase.from('issue_measures').select('id, role, name, unit, direction').eq('organization_id', organizationId).eq('issue_id', issueId),
    supabase.from('issue_measure_points').select('id, measure_id, point_kind, value, occurred_on, created_at').eq('organization_id', organizationId).eq('issue_id', issueId),
    supabase.from('issue_strata').select('id, dimension, position').eq('organization_id', organizationId).eq('issue_id', issueId).order('position'),
    supabase.from('issue_stratum_slices').select('id, stratum_id, label, occurrence_count, amount, measure_id').eq('organization_id', organizationId).eq('issue_id', issueId),
    supabase.from('issue_gemba_notes').select('id, fact, place, observed_at, observer_id').eq('organization_id', organizationId).eq('issue_id', issueId).order('observed_at', { ascending: false }),
  ]);
  if (measuresResult.error) fail(measuresResult.error, 'Não foi possível ler as medidas.');
  if (pointsResult.error) fail(pointsResult.error, 'Não foi possível ler os pontos.');
  if (strataResult.error) fail(strataResult.error, 'Não foi possível ler a estratificação.');
  if (slicesResult.error) fail(slicesResult.error, 'Não foi possível ler as fatias.');
  if (gembaResult.error) fail(gembaResult.error, 'Não foi possível ler o Gemba.');

  const points = ((pointsResult.data ?? []) as IssueMeasurePointRow[]).map(toRecord);
  const measures = ((measuresResult.data ?? []) as Array<{
    id: string;
    role: MeasureRole;
    name: string;
    unit: string;
    direction: MeasureDirection;
  }>).map((row) => ({
    id: row.id,
    role: row.role,
    name: row.name,
    unit: row.unit,
    direction: row.direction,
    points: points.filter((point) => point.measureId === row.id),
  }));

  const slices = (slicesResult.data ?? []) as Array<{
    id: string;
    stratum_id: string;
    label: string;
    occurrence_count: number | string | null;
    amount: number | string | null;
    measure_id: string | null;
  }>;
  const strata = ((strataResult.data ?? []) as Array<{ id: string; dimension: string }>).map((row) => ({
    id: row.id,
    dimension: row.dimension,
    slices: slices.filter((slice) => slice.stratum_id === row.id).map((slice) => ({
      id: slice.id,
      label: slice.label,
      occurrenceCount: numeric(slice.occurrence_count),
      amount: numeric(slice.amount),
      measureId: slice.measure_id,
    })),
  }));

  const gemba = ((gembaResult.data ?? []) as Array<{
    id: string;
    fact: string;
    place: string | null;
    observed_at: string;
    observer_id: string | null;
  }>).map((row) => ({
    id: row.id,
    fact: row.fact,
    place: row.place,
    observedAt: row.observed_at,
    observerId: row.observer_id,
  }));

  return { measures, strata, gemba };
}

async function insertPoint(input: {
  organizationId: string;
  issueId: string;
  measureId: string;
  pointKind: MeasurePointKind;
  value: number;
  occurredOn: string;
}): Promise<void> {
  const { error } = await supabase.from('issue_measure_points').insert({
    measure_id: input.measureId,
    issue_id: input.issueId,
    organization_id: input.organizationId,
    point_kind: input.pointKind,
    value: input.value,
    occurred_on: input.occurredOn,
  });
  if (error) fail(error, 'Não foi possível registrar o valor.');
}

export async function createLossMeasure(input: {
  organizationId: string;
  issueId: string;
  name: string;
  unit: string;
  value: number;
  occurredOn: string;
}): Promise<void> {
  const { data, error } = await supabase.from('issue_measures').insert({
    issue_id: input.issueId,
    organization_id: input.organizationId,
    role: 'loss',
    name: input.name.trim(),
    unit: input.unit.trim(),
    direction: 'lower_is_better',
  }).select('id').single();
  if (error || !data) fail(error, 'Não foi possível registrar a perda.');
  try {
    await insertPoint({
      organizationId: input.organizationId,
      issueId: input.issueId,
      measureId: data.id,
      pointKind: 'actual',
      value: input.value,
      occurredOn: input.occurredOn,
    });
  } catch (pointError) {
    await supabase.from('issue_measures').delete().eq('id', data.id);
    throw pointError;
  }
}

export async function createControlMeasure(input: {
  organizationId: string;
  issueId: string;
  name: string;
  unit: string;
  direction: MeasureDirection;
  baseline: number;
  target: number;
  occurredOn: string;
}): Promise<void> {
  const { data, error } = await supabase.from('issue_measures').insert({
    issue_id: input.issueId,
    organization_id: input.organizationId,
    role: 'control',
    name: input.name.trim(),
    unit: input.unit.trim(),
    direction: input.direction,
  }).select('id').single();
  if (error || !data) fail(error, 'Não foi possível registrar o item de controle.');
  try {
    await insertPoint({
      organizationId: input.organizationId,
      issueId: input.issueId,
      measureId: data.id,
      pointKind: 'baseline',
      value: input.baseline,
      occurredOn: input.occurredOn,
    });
    await insertPoint({
      organizationId: input.organizationId,
      issueId: input.issueId,
      measureId: data.id,
      pointKind: 'target',
      value: input.target,
      occurredOn: input.occurredOn,
    });
  } catch (pointError) {
    await supabase.from('issue_measures').delete().eq('id', data.id);
    throw pointError;
  }
}

export async function addMeasurePoint(input: {
  organizationId: string;
  issueId: string;
  measureId: string;
  pointKind: MeasurePointKind;
  value: number;
  occurredOn: string;
}): Promise<void> {
  await insertPoint(input);
}

export async function deleteIssueMeasure(organizationId: string, measureId: string): Promise<void> {
  const { error } = await supabase.from('issue_measures').delete().eq('organization_id', organizationId).eq('id', measureId);
  if (error) fail(error, 'Não foi possível excluir a medida.');
}

export async function createStratum(input: {
  organizationId: string;
  issueId: string;
  dimension: string;
  position: number;
}): Promise<void> {
  const { error } = await supabase.from('issue_strata').insert({
    issue_id: input.issueId,
    organization_id: input.organizationId,
    dimension: input.dimension.trim(),
    position: input.position,
  });
  if (error) fail(error, 'Não foi possível criar a estratificação.');
}

export async function deleteStratum(organizationId: string, stratumId: string): Promise<void> {
  const { error } = await supabase.from('issue_strata').delete().eq('organization_id', organizationId).eq('id', stratumId);
  if (error) fail(error, 'Não foi possível excluir a estratificação.');
}

export async function createSlice(input: {
  organizationId: string;
  issueId: string;
  stratumId: string;
  label: string;
  occurrenceCount: number | null;
  amount: number | null;
  measureId: string | null;
}): Promise<void> {
  if (input.amount != null && !input.measureId) {
    throw new Error('O valor precisa de uma medida deste problema.');
  }
  const { error } = await supabase.from('issue_stratum_slices').insert({
    stratum_id: input.stratumId,
    issue_id: input.issueId,
    organization_id: input.organizationId,
    label: input.label.trim(),
    occurrence_count: input.occurrenceCount,
    amount: input.amount,
    measure_id: input.measureId,
  });
  if (error) fail(error, 'Não foi possível registrar a fatia.');
}

export async function deleteSlice(organizationId: string, sliceId: string): Promise<void> {
  const { error } = await supabase.from('issue_stratum_slices').delete().eq('organization_id', organizationId).eq('id', sliceId);
  if (error) fail(error, 'Não foi possível excluir a fatia.');
}

export async function createGembaNote(input: {
  organizationId: string;
  issueId: string;
  fact: string;
  place: string;
  observedAt: string;
  observerId: string;
}): Promise<void> {
  const { error } = await supabase.from('issue_gemba_notes').insert({
    issue_id: input.issueId,
    organization_id: input.organizationId,
    fact: input.fact.trim(),
    place: input.place.trim() || null,
    observed_at: input.observedAt,
    observer_id: input.observerId,
  });
  if (error) fail(error, 'Não foi possível registrar a observação.');
}

export async function deleteGembaNote(organizationId: string, noteId: string): Promise<void> {
  const { error } = await supabase.from('issue_gemba_notes').delete().eq('organization_id', organizationId).eq('id', noteId);
  if (error) fail(error, 'Não foi possível excluir a observação.');
}

interface CauseCheckRow {
  cause_effect: CauseCheckAnswer;
  explains_phenomenon: CauseCheckAnswer;
  technically_blockable: CauseCheckAnswer;
  unwanted_effects: CauseCheckAnswer;
  cause_effect_note: string | null;
  explains_phenomenon_note: string | null;
  technically_blockable_note: string | null;
  unwanted_effects_note: string | null;
}

function causeCheckFromRow(row: CauseCheckRow): CauseCheckView {
  return {
    causeEffect: row.cause_effect,
    explainsPhenomenon: row.explains_phenomenon,
    technicallyBlockable: row.technically_blockable,
    unwantedEffects: row.unwanted_effects,
    causeEffectNote: row.cause_effect_note ?? '',
    explainsPhenomenonNote: row.explains_phenomenon_note ?? '',
    technicallyBlockableNote: row.technically_blockable_note ?? '',
    unwantedEffectsNote: row.unwanted_effects_note ?? '',
  };
}

/** Lê o teste de consistência. A linha não escreve causa, status nem root_cause. */
export async function readCauseCheck(organizationId: string, issueId: string): Promise<CauseCheckView | null> {
  const { data, error } = await supabase
    .from('issue_cause_checks')
    .select('cause_effect, explains_phenomenon, technically_blockable, unwanted_effects, cause_effect_note, explains_phenomenon_note, technically_blockable_note, unwanted_effects_note')
    .eq('organization_id', organizationId)
    .eq('issue_id', issueId)
    .maybeSingle();
  if (error) fail(error, 'Não foi possível ler o teste de consistência.');
  return data ? causeCheckFromRow(data as CauseCheckRow) : null;
}

/** Grava as quatro respostas humanas. Não altera o problema. */
export async function saveCauseCheck(input: {
  organizationId: string;
  issueId: string;
  reviewedBy: string;
  check: CauseCheckView;
}): Promise<void> {
  const { error } = await supabase.from('issue_cause_checks').upsert({
    issue_id: input.issueId,
    organization_id: input.organizationId,
    cause_effect: input.check.causeEffect,
    explains_phenomenon: input.check.explainsPhenomenon,
    technically_blockable: input.check.technicallyBlockable,
    unwanted_effects: input.check.unwantedEffects,
    cause_effect_note: input.check.causeEffectNote.trim() || null,
    explains_phenomenon_note: input.check.explainsPhenomenonNote.trim() || null,
    technically_blockable_note: input.check.technicallyBlockableNote.trim() || null,
    unwanted_effects_note: input.check.unwantedEffectsNote.trim() || null,
    reviewed_by: input.reviewedBy,
    reviewed_at: new Date().toISOString(),
  }, { onConflict: 'issue_id' });
  if (error) fail(error, 'Não foi possível registrar o teste de consistência.');
}

export interface IssueMaspEventRecord {
  id: string;
  kind: 'training' | 'deviation';
  title: string | null;
  body: string | null;
  occurredOn: string | null;
  createdAt: string;
}

interface IssueMaspEventRow {
  id: string;
  kind: 'training' | 'deviation';
  title: string | null;
  body: string | null;
  occurred_on: string | null;
  created_at: string;
}

/** Treinamento e desvio pertencem ao problema. A tabela não vincula contramedida. */
export async function listIssueMaspEvents(
  organizationId: string,
  issueId: string,
): Promise<IssueMaspEventRecord[]> {
  const { data, error } = await supabase
    .from('issue_masp_events')
    .select('id, kind, title, body, occurred_on, created_at')
    .eq('organization_id', organizationId)
    .eq('issue_id', issueId)
    .in('kind', ['training', 'deviation'])
    .order('created_at', { ascending: false });
  if (error) fail(error, 'Não foi possível ler os eventos da execução.');
  return ((data ?? []) as IssueMaspEventRow[]).map((row) => ({
    id: row.id,
    kind: row.kind,
    title: row.title,
    body: row.body,
    occurredOn: row.occurred_on,
    createdAt: row.created_at,
  }));
}

/** Não cria Action e não altera o status do problema. */
export async function createIssueMaspEvent(input: {
  organizationId: string;
  issueId: string;
  kind: 'training' | 'deviation';
  title: string;
  body: string;
  occurredOn: string | null;
  actorId: string | null;
}): Promise<void> {
  const title = input.title.trim() || null;
  const body = input.body.trim() || null;
  if (input.kind === 'training' && !title) throw new Error('Informe o treinamento.');
  if (input.kind === 'deviation' && !body) throw new Error('Informe o desvio.');
  const { error } = await supabase.from('issue_masp_events').insert({
    issue_id: input.issueId,
    organization_id: input.organizationId,
    kind: input.kind,
    title,
    body,
    occurred_on: input.occurredOn,
    actor_id: input.actorId,
  });
  if (error) fail(error, 'Não foi possível registrar o evento.');
}

export interface StandardizationEventRecord {
  id: string;
  kind: 'standard' | 'training' | 'communication' | 'audit';
  title: string | null;
  body: string | null;
  audience: string | null;
  result: 'pass' | 'fail' | 'partial' | null;
  occurredOn: string | null;
  createdAt: string;
}

interface StandardizationEventRow {
  id: string;
  kind: StandardizationEventRecord['kind'];
  title: string | null;
  body: string | null;
  audience: string | null;
  result: StandardizationEventRecord['result'];
  occurred_on: string | null;
  created_at: string;
}

/** Padrão, treinamento, comunicação e auditoria. Não altera eficácia nem status. */
export async function listStandardizationEvents(
  organizationId: string,
  issueId: string,
): Promise<StandardizationEventRecord[]> {
  const { data, error } = await supabase
    .from('issue_masp_events')
    .select('id, kind, title, body, audience, result, occurred_on, created_at')
    .eq('organization_id', organizationId)
    .eq('issue_id', issueId)
    .in('kind', ['standard', 'training', 'communication', 'audit'])
    .order('occurred_on', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });
  if (error) fail(error, 'Não foi possível ler a padronização.');
  return ((data ?? []) as StandardizationEventRow[]).map((row) => ({
    id: row.id,
    kind: row.kind,
    title: row.title,
    body: row.body,
    audience: row.audience,
    result: row.result,
    occurredOn: row.occurred_on,
    createdAt: row.created_at,
  }));
}

/** Não cria Action, não grava eficácia e não altera o status do problema. */
export async function createStandardizationEvent(input: {
  organizationId: string;
  issueId: string;
  kind: StandardizationEventRecord['kind'];
  title?: string | null;
  body?: string | null;
  audience?: string | null;
  result?: string | null;
  occurredOn: string | null;
  actorId: string | null;
}): Promise<void> {
  const payload = standardizationEventPayload(input);
  const { error } = await supabase.from('issue_masp_events').insert({
    issue_id: input.issueId,
    organization_id: input.organizationId,
    kind: input.kind,
    title: payload.title,
    body: payload.body,
    audience: payload.audience,
    result: payload.result,
    occurred_on: input.occurredOn,
    actor_id: input.actorId,
  });
  if (error) fail(error, 'Não foi possível registrar a padronização.');
}
